import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ConsentService } from '@/services/ChatConsentService';

const MAX_RECORD_ATTEMPTS = 3;
const RETRY_DELAY_MS = 3_000;

// Consent is recorded once both acceptance and the conversation id are known.
const useChatConsent = ({ recruitmentId, conversationId, enabled }) => {
    const { i18n } = useTranslation('chat');
    const language = i18n.language;
    const [settings, setSettings] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [accepted, setAccepted] = useState(null);
    const recordedRef = useRef(false);
    const settingsRef = useRef(null);
    settingsRef.current = settings;

    useEffect(() => {
        if (!enabled || !recruitmentId) return undefined;
        let cancelled = false;
        setIsLoading(true);
        ConsentService.getSettings(recruitmentId, language)
            .then(data => { if (!cancelled) setSettings(data); })
            .catch(() => { if (!cancelled) setSettings({}); })
            .finally(() => { if (!cancelled) setIsLoading(false); });
        return () => { cancelled = true; };
    }, [enabled, recruitmentId, language]);

    useEffect(() => {
        if (!accepted || !conversationId || !recruitmentId || recordedRef.current) return undefined;
        recordedRef.current = true;
        let cancelled = false;
        let timer = null;

        const attempt = (n) => {
            ConsentService.record({
                ...accepted,
                recruitmentId,
                conversationId,
                aiDisclosureVersion: settingsRef.current?.aiDisclosureVersion,
            })
                .catch(err => {
                    console.error('[Consent] failed to record', err);
                    if (!cancelled && n < MAX_RECORD_ATTEMPTS) {
                        timer = setTimeout(() => attempt(n + 1), RETRY_DELAY_MS * n);
                    }
                });
        };
        attempt(1);

        return () => { cancelled = true; clearTimeout(timer); };
    }, [accepted, conversationId, recruitmentId]);

    const accept = useCallback((payload) => setAccepted(payload), []);

    return { settings, isLoading, isAccepted: Boolean(accepted), accept };
};

export default useChatConsent;
